import type { AxiosError } from "axios";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { MetaChipTone } from "../../../../../components/UIToolkit/Badges/MetaChip";
import type { RecordSetDiffLabels } from "../../../../../components/UIToolkit/Tables/RecordSetDiff/RecordSetDiff";
import type { DiffRecord } from "../../../../../components/UIToolkit/Tables/RecordSetDiff/recordSetDiffModel";
import type { AirtableExtractionState, AirtableRowKind } from "./airtableImportTypes";

/** i18n key naming each row kind, for the per-row provenance chip and the summary counts. */
export const AIRTABLE_KIND_LABEL_KEY: Record<AirtableRowKind, string> = {
	deterministic: "department.config.airtable.kind.deterministic",
	llm: "department.config.airtable.kind.llm",
	unresolvable: "department.config.airtable.kind.unresolvable",
	out_of_band: "department.config.airtable.kind.outOfBand",
	blocked: "department.config.airtable.kind.blocked",
	protected: "department.config.airtable.kind.protected",
};

/**
 * Tone per kind. `deterministic` is the quiet one on purpose — it is the majority of rows and the one
 * needing the least attention, so it must not out-shout the rows that do. `out_of_band` wears error
 * rather than warning: a bed count of 125100 is a defect upstream, not a caveat.
 *
 * `protected` wears WARNING where `blocked` wears error, and the difference is the message: `blocked`
 * is a refusal about this department's data ("applying this would strand shifts already on the books"),
 * `protected` is a standing property of the field ("this flow is never allowed to write it"). Nothing
 * is wrong — the two values simply disagree and no one here can settle it — so it flags rather than
 * alarms.
 */
export const AIRTABLE_KIND_TONE: Record<AirtableRowKind, MetaChipTone> = {
	deterministic: "neutral",
	llm: "primary",
	unresolvable: "neutral",
	out_of_band: "error",
	blocked: "error",
	protected: "warning",
};

/** How complete a model's answer was, for the expanded provenance panel. */
export const AIRTABLE_EXTRACTION_LABEL_KEY: Record<AirtableExtractionState, string> = {
	extracted: "department.config.airtable.extraction.extracted",
	partial: "department.config.airtable.extraction.partial",
};

export const AIRTABLE_EXTRACTION_TONE: Record<AirtableExtractionState, MetaChipTone> = {
	extracted: "success",
	partial: "warning",
};

/** An already-translatable message: the i18n key plus whatever the backend attached to interpolate. */
export type AirtableErrorMessage = { key: string; params: Record<string, unknown> };

/** The `UIVisibleError` the backend raises when the Airtable record moved under the operator's feet. */
export const AIRTABLE_RECORD_CHANGED_KEY = "airtable.import.record.changed";

/**
 * The message to show for a failed Airtable import request.
 *
 * Prefers the backend's `UIVisibleError` payload, passing `details` through as interpolation values, so
 * "this Airtable record matched more than one facility" can name them. Anything else collapses to one
 * generic sentence rather than rendering a raw exception: FastAPI's own 401/403/422 bodies are internal
 * prose ("Only support users can preview an Airtable config"), not copy for a user.
 */
export function airtableErrorMessage(error: unknown): AirtableErrorMessage {
	const detail = (error as AxiosError<{ detail?: { message?: string; error_type?: string; details?: Record<string, unknown> } | string }>)?.response?.data
		?.detail;
	if (detail && typeof detail === "object" && detail.error_type === "UIVisibleError" && detail.message) {
		return { key: detail.message, params: detail.details ?? {} };
	}
	return { key: "department.config.airtable.error.requestFailed", params: {} };
}

/** True when the failure is specifically "the Airtable row changed since the preview". */
export function isRecordChangedError(error: unknown): boolean {
	return airtableErrorMessage(error).key === AIRTABLE_RECORD_CHANGED_KEY;
}

/**
 * Render one config value as the display LINES a `ValueDiffPair` compares — one entry per list item, so
 * a reviewer sees the whole before and the whole after rather than a count.
 *
 * This is the ONLY renderer: the backend sends raw JSON values and no pre-rendered display strings.
 *
 * Object keys are emitted verbatim (`bonusVal: 12`). They are DATA, not UI copy: this flow can carry any
 * config field, including nested rule shapes whose keys the app has never had a label for, and inventing
 * a translation per key would mean a silently-unlabelled field the first time the backend adds one. A
 * support-only tool showing the stored key is honest; guessing is not. Known limitation: a nested rule
 * therefore reads a little like JSON.
 */
export function valueLines(value: unknown, t: TFunction): string[] {
	if (value === null || value === undefined || value === "") return [];
	if (typeof value === "boolean") return [value ? t("yes") : t("no")];
	if (typeof value === "number" || typeof value === "string") return [String(value)];
	if (DateTime.isDateTime(value)) return [value.toFormat("yyyy-MM-dd")];
	if (Array.isArray(value)) return value.flatMap((item) => (item === null || item === undefined ? [] : [formatScalarOrObject(item, t)]));
	if (typeof value === "object") return Object.entries(value as Record<string, unknown>).map(([key, entry]) => `${key}: ${formatScalarOrObject(entry, t)}`);
	return [String(value)];
}

/**
 * Members of a rule object that carry no decision, dropped before the line is built.
 *
 * `id` / `departmentId` / `createdAt` / `updatedAt` are server-owned identity and audit columns. Left
 * in, they are actively harmful rather than merely noisy: a 12-key break rule leads with a 36-character
 * UUID, pushing the two numbers the reviewer is actually deciding about (`breakLimit`, `breakTime`) off
 * the end of a wrapped line — and since an incoming rule carries `id: null` where the current one has a
 * real UUID, every rule reads as wholly rewritten even when one threshold moved. `departmentId` is the
 * same string on both sides of every row in the diff, by construction.
 */
const OMITTED_RULE_MEMBERS = new Set(["id", "departmentId", "createdAt", "updatedAt"]);

/**
 * The members of one rule object worth showing, as `[key, displayValue]` pairs.
 *
 * The single place the "what does a rule member have to be to be worth a reader's attention" rule
 * lives: both the flat one-line rendering (`formatScalarOrObject`) and the paired record rendering
 * (`recordSetsFrom`) read it, so the two can never disagree about which members exist.
 */
function ruleMemberEntries(item: Record<string, unknown>): [string, string][] {
	return Object.entries(item)
		.filter(([key, entry]) => {
			if (OMITTED_RULE_MEMBERS.has(key)) return false;
			if (entry === null || entry === undefined || entry === "") return false;
			// An empty array is the models' canonical "applies to all" marker. Rendering it produces a
			// bare `roles: ·` — three of those per break rule, saying nothing.
			return !(Array.isArray(entry) && entry.length === 0);
		})
		.map(([key, entry]) => [key, Array.isArray(entry) ? entry.join(", ") : String(entry)]);
}

/** One list ITEM on one line — an object collapses to `key: value · key: value` rather than wrapping. */
function formatScalarOrObject(item: unknown, t: TFunction): string {
	if (item === null || item === undefined) return "—";
	if (typeof item === "boolean") return item ? t("yes") : t("no");
	if (typeof item === "object" && !Array.isArray(item)) {
		const parts = ruleMemberEntries(item as Record<string, unknown>).map(([key, entry]) => `${key}: ${entry}`);
		return parts.length > 0 ? parts.join(" · ") : "—";
	}
	if (Array.isArray(item)) return item.join(", ");
	return String(item);
}

/** The member a rule is known by, and therefore the one the two sides are PAIRED on. */
const RECORD_TITLE_MEMBER = "name";

/** True for a plain JSON object — the shape a rule arrives as. Arrays and `null` are not. */
function isRuleObject(item: unknown): item is Record<string, unknown> {
	return typeof item === "object" && item !== null && !Array.isArray(item);
}

/** A rule's name as a pairing identity: case and surrounding space are not what makes two rules differ. */
function nameIdentity(item: Record<string, unknown>): string | null {
	const rawName = item[RECORD_TITLE_MEMBER];
	return typeof rawName === "string" && rawName.trim() !== "" ? rawName.trim().toLowerCase() : null;
}

/** Name identities that occur exactly once in `items` — the only ones that can identify a single rule. */
function uniqueNameIdentities(items: Record<string, unknown>[]): Set<string> {
	const counts = new Map<string, number>();
	for (const item of items) {
		const identity = nameIdentity(item);
		if (identity !== null) counts.set(identity, (counts.get(identity) ?? 0) + 1);
	}
	return new Set([...counts].flatMap(([identity, count]) => (count === 1 ? [identity] : [])));
}

/** A list of rules, or `null` for any other shape (a scalar, a list of strings, an object of arrays). */
function ruleList(value: unknown): Record<string, unknown>[] | null {
	return Array.isArray(value) && value.every(isRuleObject) ? value : null;
}

/** One side as `DiffRecord`s. Only a rule whose identity is in `pairable` gets a key the other side can share. */
function recordsFrom(items: Record<string, unknown>[], t: TFunction, side: "current" | "incoming", pairable: ReadonlySet<string>): DiffRecord[] {
	return items.map((item, index) => {
		const rawName = item[RECORD_TITLE_MEMBER];
		const name = typeof rawName === "string" && rawName !== "" ? rawName : null;
		const identity = nameIdentity(item);
		const members = ruleMemberEntries(item)
			// The name is the record's heading; repeating it as a member is noise on every card.
			.filter(([key]) => name === null || key !== RECORD_TITLE_MEMBER)
			.map(([key, entry]) => ({ key, label: key, value: entry }));
		return {
			matchKey: identity !== null && pairable.has(identity) ? `name:${identity}` : `${side}:${index}`,
			title: name ?? t("department.config.airtable.rule.unnamed", { index: index + 1 }),
			members,
		};
	});
}

/**
 * Both sides of a rule-list field as `DiffRecord`s, or `null` when either value is not a list of rules.
 *
 * `null` is what routes a field to `ValueDiffPair` instead: a scalar, a list of strings (`jobIds`) or
 * an object-of-arrays (`fullShiftTimes`) has no records to pair, and the flat before/after is the right
 * rendering for it. An EMPTY list is not `null` — it is a legitimate side, and the emptier side of a
 * replacement is exactly the case worth showing.
 *
 * Two rules are PAIRED only when they share a name that is unambiguous: present exactly once on each
 * side. Anything else — no name, a name held twice on either side, a name only one side has — gets a
 * key that cannot equal anything on the other side, so it reads as one rule removed and one added.
 * Position is not a correspondence, and neither is the first of two same-named rules: claiming "this
 * member moved" between two rules we merely guessed belong together would be a false statement in a
 * flow that then writes the result. Accepting replaces the whole list either way, so removed/added is
 * never wrong — only less compact.
 */
export function recordSetsFrom(current: unknown, incoming: unknown, t: TFunction): { current: DiffRecord[]; incoming: DiffRecord[] } | null {
	const currentRules = ruleList(current);
	const incomingRules = ruleList(incoming);
	if (currentRules === null || incomingRules === null) return null;
	const incomingUnique = uniqueNameIdentities(incomingRules);
	const pairable = new Set([...uniqueNameIdentities(currentRules)].filter((identity) => incomingUnique.has(identity)));
	return {
		current: recordsFrom(currentRules, t, "current", pairable),
		incoming: recordsFrom(incomingRules, t, "incoming", pairable),
	};
}

/**
 * Every string `RecordSetDiff` renders for this flow. Built here rather than in the cell so the wording
 * of a rule diff lives beside the wording of everything else in the import.
 */
export function recordDiffLabels(t: TFunction): RecordSetDiffLabels {
	return {
		replaceTitle: t("department.config.airtable.records.replaceTitle"),
		destructionBody: t("department.config.airtable.records.destruction"),
		state: {
			removed: t("department.config.airtable.records.state.removed"),
			added: t("department.config.airtable.records.state.added"),
			changed: t("department.config.airtable.records.state.changed"),
			unchanged: t("department.config.airtable.records.state.unchanged"),
		},
		currentCaption: t("department.config.airtable.column.current"),
		incomingCaption: t("department.config.airtable.column.incoming"),
		unchangedMembersLabel: (count: number) => t("department.config.airtable.records.unchangedMembers", { count }),
		absentValue: t("department.config.airtable.value.empty"),
		listAriaLabel: t("department.config.airtable.records.listAriaLabel"),
	};
}
